/**
 * Wizard 2 State Manager
 *
 * Same as wizard-1 state but with:
 * - selectedFlavorsTrial, selectedFlavorsFull (separate arrays)
 * - selectedPlan: bulk multiplier only — '1', '2', or '3'
 * - planBowlValue: Shopify "Kom" option value (e.g. Volledig / Half), from product options
 */

function normalizePetNameEdges(value) {
  if (typeof value !== 'string') return value;
  return value.replace(/^[\s\uFEFF\u00A0]+|[\s\uFEFF\u00A0]+$/g, '');
}
window.normalizePetNameEdges = normalizePetNameEdges;

class PetFlowState {
  constructor(flowName = 'wizard-4') {
    this.flowName = flowName;
    this.currentStep = 'name';
    this.petData = {
      name: '',
      gender: '',
      spayed: null,
      ageGroup: '',
      breed: '',
      breedUnknown: false,
      breedMixed: false,
      weight: null,
      composition: '',
      activity: '',
      isWorkingDog: false,
      allergies: [],
      snacks: '',
      ownerName: '',
      ownerEmail: '',
      emailConsent: true,
      profilePhotoUrl: '',
    };

    this.calculations = {
      dailyKcal: null,
      dailyFoodG: null,
      daysPerBag: null,
      bagCount: null,
      matchDays: null,
      introBagCount: null,
      introMatchDays: null
    };

    this.selectedFlavorsTrial = []; // Trial delivery flavors (rund, kip, vis)
    this.selectedFlavorsFull = [];  // Full plan flavors
    this.selectedPlan = null;      // '1', '2', '3' — bulk / shipment frequency multiplier only
    /** @type {string|null} Selected Shopify product option value for bowl size (Kom), storefront-translated */
    this.planBowlValue = null;
    /** @type {string[]|null} Ordered values from Shopify for the bowl option (for half detection) */
    this.planBowlOptionValues = null;
    this.selectedPricePeriod = 'day';
    this.petMetaobjectId = null;
    this.customerId = null;
    this.sessionCustomerId = null;
    this.sessionOwnerName = null;
    this.sessionOwnerEmail = null;
    this.sessionEmailConsent = true;
    /** User continued past the email step without an address (Wizard 2 only). */
    this.skippedEmailWithoutAddress = false;
    /**
     * Last explicit marketing consent choice on the email step (Wizard 2).
     * null = no choice yet → UI defaults to opted in when the consent row is shown.
     */
    this.wizardEmailMarketingConsentPreference = null;
    this.variantPricing = {};
    this.variantPricingHalf = {};
    /** ISO 4217 from Storefront variant prices (e.g. HUF); fallback to Shopify.currency.active in formatters */
    this.presentmentCurrencyCode = null;
    this.sellingPlanNames = {};
    /** Last trial-bundle discount % shown in the offer bar (after plan_overview succeeded). */
    this.wizardOfferBarPct = null;
    /** `buildWizardPricingFingerprint` output when wizardOfferBarPct was saved. */
    this.wizardOfferBarFingerprint = null;
    this.dogs = [];
    this.removedDogs = [];
    this.largeDogOffer = null;
    this.largeDogOfferDeadline = null;
    this.largeDogOfferWarned = false;
    this.planPortion = 'complete';
    this.boxSize = 'medium';
    this.lifetimeGiftId = 'lifetime_lamb_tripe';
    this.scoopOn = true;
    this.extrasOn = {};
    this.freeGiftOff = '';
  }

  blankDog(source) {
    const src = source || {};
    return {
      name: src.name || '',
      gender: src.gender || '',
      spayed: src.spayed === undefined ? null : src.spayed,
      ageGroup: src.ageGroup || '',
      breed: src.breed || '',
      breedUnknown: !!src.breedUnknown,
      breedMixed: !!src.breedMixed,
      weight: src.weight === undefined ? null : src.weight,
      composition: src.composition || '',
      activity: src.activity || '',
      allergies: Array.isArray(src.allergies) ? src.allergies.slice() : [],
      currentFood: Array.isArray(src.currentFood) ? src.currentFood.slice() : [],
      eating: src.eating || '',
      health: src.health || '',
      healthIssues: Array.isArray(src.healthIssues) ? src.healthIssues.slice() : [],
      snacks: src.snacks || '',
      flavors: Array.isArray(src.flavors) ? src.flavors.slice() : [],
      flavorsDefaulted: !!src.flavorsDefaulted,
      flavorsTouched: !!src.flavorsTouched,
      portion: src.portion === 'half' || src.portion === 'topper' ? src.portion : 'complete',
      petMetaobjectId: src.petMetaobjectId || null,
      recalledKey: src.recalledKey || null
    };
  }

  ensureDogs() {
    if (!Array.isArray(this.dogs) || this.dogs.length === 0) {
      const dog = this.blankDog(this.petData);
      if (this.petMetaobjectId) dog.petMetaobjectId = this.petMetaobjectId;
      this.dogs = [dog];
    }
    return this.dogs;
  }

  syncLeadDog() {
    const dog = this.ensureDogs()[0];
    if (!dog) return;
    this.petData.name = dog.name || '';
    this.petData.gender = dog.gender || '';
    this.petData.spayed = dog.spayed === undefined ? null : dog.spayed;
    this.petData.ageGroup = dog.ageGroup || '';
    this.petData.breed = dog.breed || '';
    this.petData.breedUnknown = !!dog.breedUnknown;
    this.petData.breedMixed = !!dog.breedMixed;
    this.petData.weight = dog.weight === '' || dog.weight === undefined ? null : dog.weight;
    this.petData.composition = dog.composition || '';
    this.petData.activity = dog.activity || '';
    this.petData.allergies = Array.isArray(dog.allergies) ? dog.allergies.slice() : [];
    this.petData.currentFood = Array.isArray(dog.currentFood) ? dog.currentFood.slice() : [];
    this.petData.eating = dog.eating || '';
    this.petData.health = dog.health || '';
    this.petData.healthIssues = Array.isArray(dog.healthIssues) ? dog.healthIssues.slice() : [];
    this.petData.snacks = dog.snacks || '';
    if (dog.petMetaobjectId) this.petMetaobjectId = dog.petMetaobjectId;
  }

  setDogField(index, field, value) {
    const dogs = this.ensureDogs();
    const dog = dogs[index];
    if (!dog || !(field in dog)) return;
    if (field === 'name' && typeof value === 'string') {
      value = normalizePetNameEdges(value);
    }
    if (field === 'weight') {
      const n = parseFloat(value);
      value = Number.isFinite(n) ? n : null;
    }
    dog[field] = value;
    if (field === 'breedUnknown' && value) dog.breed = 'dont_know';
    if (field === 'breedMixed' && value) dog.breed = 'mixed';
    if (index === 0) this.syncLeadDog();
    this.notifyTraits();
  }

  toggleDogList(index, field, value, checked) {
    const dogs = this.ensureDogs();
    const dog = dogs[index];
    if (!dog || !Array.isArray(dog[field])) return;
    const list = dog[field];
    const at = list.indexOf(value);
    if (checked && at === -1) list.push(value);
    if (!checked && at > -1) list.splice(at, 1);
    if (index === 0) this.syncLeadDog();
    this.notifyTraits();
  }

  addDog() {
    const dogs = this.ensureDogs();
    if (dogs.length >= 20) return dogs;
    dogs.push(this.blankDog());
    this.notifyTraits();
    return dogs;
  }

  removeDog(index) {
    const dogs = this.ensureDogs();
    if (dogs.length <= 1 || index < 0 || index >= dogs.length) return dogs;
    const removed = this.blankDog(dogs[index]);
    const key = this.dogNameKey(removed.name);
    if (key) {
      this.removedDogs = (this.removedDogs || []).filter((dog) => this.dogNameKey(dog.name) !== key);
      this.removedDogs.push(removed);
    }
    dogs.splice(index, 1);
    this.syncLeadDog();
    this.petMetaobjectId = dogs[0].petMetaobjectId || null;
    this.notifyTraits();
    return dogs;
  }

  dogNameKey(name) {
    return normalizePetNameEdges(name || '').toLowerCase();
  }

  dogHasQuizAnswers(dog) {
    if (!dog) return false;
    return !!(
      dog.gender ||
      dog.spayed === true ||
      dog.spayed === false ||
      dog.ageGroup ||
      dog.breed ||
      dog.breedUnknown ||
      dog.breedMixed ||
      (dog.weight != null && dog.weight !== '') ||
      dog.composition ||
      dog.activity ||
      dog.snacks ||
      (Array.isArray(dog.allergies) && dog.allergies.length) ||
      dog.petMetaobjectId
    );
  }

  prefillFromRemoved(index) {
    const dogs = this.ensureDogs();
    const dog = dogs[index];
    if (!dog) return false;
    const key = this.dogNameKey(dog.name);
    const taken = key && dogs.some((other, otherIndex) => otherIndex !== index && this.dogNameKey(other.name) === key);
    const recalled = !taken && key
      ? (this.removedDogs || []).find((item) => this.dogNameKey(item.name) === key)
      : null;
    if (recalled && !this.dogHasQuizAnswers(dog)) {
      const restored = this.blankDog(recalled);
      restored.name = dog.name;
      restored.recalledKey = key;
      dogs[index] = restored;
      if (index === 0) this.syncLeadDog();
      return true;
    }
    if (dog.recalledKey && dog.recalledKey !== key) {
      const fresh = this.blankDog();
      fresh.name = dog.name;
      dogs[index] = fresh;
      if (index === 0) this.syncLeadDog();
    }
    return false;
  }

  combinedWeightKg() {
    return this.ensureDogs().reduce((sum, dog) => sum + (Number(dog.weight) || 0), 0);
  }

  updatePetData(field, value) {
      if (field in this.petData) {
      if (field === 'name' && typeof value === 'string') {
        value = normalizePetNameEdges(value);
      }
      this.petData[field] = value;
      if (Array.isArray(this.dogs) && this.dogs[0] && field in this.dogs[0]) {
        this.dogs[0][field] = value;
      }
      this.notifyTraits();
    } else {
      console.warn(`Unknown pet data field: ${field}`);
    }
  }

  notifyTraits() {
    try {
      if (window.WizardGa4Traits && window.WizardGa4Traits.notify) window.WizardGa4Traits.notify();
    } catch (e) {}
  }

  updatePetDataBatch(updates) {
    Object.keys(updates).forEach(field => {
      this.updatePetData(field, updates[field]);
    });
  }

  updateCalculations(calculations) {
    this.calculations = { ...this.calculations, ...calculations };
  }

  setCurrentStep(step) {
    this.currentStep = step;
  }

  getCurrentStep() {
    return this.currentStep;
  }

  validateStep(step = this.currentStep) {
    const dogs = this.ensureDogs();
    const every = (fn) => dogs.every(fn);
    switch (step) {
      case 'name': {
        const names = dogs.map((dog) => (dog.name || '').trim().toLowerCase());
        return dogs.length >= 1 && dogs.length <= 20 &&
          every((dog) => (dog.name || '').trim().length > 0) &&
          new Set(names).size === names.length;
      }
      case 'spayed':
        return every((dog) => (dog.gender === 'male' || dog.gender === 'female') &&
          (dog.spayed === true || dog.spayed === false));
      case 'age':
        return every((dog) => dog.ageGroup !== '');
      case 'breed':
        return every((dog) => (dog.breed || '').trim().length > 0 || dog.breedUnknown || dog.breedMixed);
      case 'food':
        return every((dog) => Array.isArray(dog.currentFood) && dog.currentFood.length > 0);
      case 'eating':
        return every((dog) => dog.eating !== '');
      case 'weight':
        return every((dog) => {
          const kg = Number(dog.weight);
          return Number.isFinite(kg) && kg >= 1 && kg <= 100;
        });
      case 'composition':
        return every((dog) => dog.composition !== '');
      case 'activity':
        return every((dog) => dog.activity !== '' && dog.activity !== 'workingDog');
      case 'allergies':
        return true;
      case 'issues':
        return every((dog) => dog.health === 'none' || dog.health === 'some');
      case 'snacks':
        return every((dog) => dog.snacks !== '');
      case 'email':
        if (this.skippedEmailWithoutAddress) return true;
        return this.isValidEmail(this.petData.ownerEmail);
      case 'lifetime_offer':
        return this.isValidEmail(this.petData.ownerEmail);
      case 'box_size':
        return this.boxSize === 'medium' || this.boxSize === 'groot' || this.boxSize === 'extra';
      default:
        return true;
    }
  }

  planDogs() {
    if (Array.isArray(this.dogs) && this.dogs.length) return this.dogs;
    const pet = this.petData;
    if (pet && (pet.name || pet.ageGroup || pet.snacks || pet.weight != null)) return [pet];
    return [];
  }

  canCalculateHousehold() {
    const dogs = this.planDogs();
    if (!dogs.length) return false;
    return dogs.every((dog) => {
      const weight = Number(dog.weight);
      return weight > 0 &&
        !!dog.ageGroup &&
        !!dog.activity &&
        dog.spayed !== null &&
        dog.spayed !== undefined &&
        !!dog.composition &&
        !!dog.snacks;
    });
  }

  firstUnansweredStep() {
    const order = (window.BzWizard && typeof window.BzWizard.domSteps === 'function')
      ? window.BzWizard.domSteps()
      : ['name'];
    const skip = new Set([
      'get_started', 'calculation', 'flavors', 'box_size',
      'plan_overview', 'plan_onetime', 'extras', 'profile',
      'lifetime_offer'
    ]);
    for (let i = 0; i < order.length; i += 1) {
      const step = order[i];
      if (skip.has(step)) continue;
      if (!this.validateStep(step)) return step;
    }
    return null;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  getPetData() {
    return { ...this.petData };
  }

  getCalculations() {
    return { ...this.calculations };
  }

  getState() {
    return {
      currentStep: this.currentStep,
      petData: this.getPetData(),
      calculations: this.getCalculations(),
      selectedFlavorsTrial: [...this.selectedFlavorsTrial],
      selectedFlavorsFull: [...this.selectedFlavorsFull],
      selectedPlan: this.selectedPlan,
      planBowlValue: this.planBowlValue,
      planBowlOptionValues: this.planBowlOptionValues ? [...this.planBowlOptionValues] : null,
      selectedPricePeriod: this.selectedPricePeriod,
      petMetaobjectId: this.petMetaobjectId,
      customerId: this.customerId,
      sessionCustomerId: this.sessionCustomerId,
      sessionOwnerName: this.sessionOwnerName,
      sessionOwnerEmail: this.sessionOwnerEmail,
      sessionEmailConsent: this.sessionEmailConsent,
      skippedEmailWithoutAddress: this.skippedEmailWithoutAddress,
      wizardEmailMarketingConsentPreference: this.wizardEmailMarketingConsentPreference,
      variantPricing: { ...this.variantPricing },
      variantPricingHalf: { ...this.variantPricingHalf },
      presentmentCurrencyCode: this.presentmentCurrencyCode,
      sellingPlanNames: { ...this.sellingPlanNames },
      wizardOfferBarPct: this.wizardOfferBarPct,
      wizardOfferBarFingerprint: this.wizardOfferBarFingerprint,
      dogs: this.ensureDogs().map((dog) => this.blankDog(dog)),
      removedDogs: (this.removedDogs || []).map((dog) => this.blankDog(dog)),
      planPortion: this.planPortion,
      boxSize: this.boxSize || 'medium',
      lifetimeGiftId: this.lifetimeGiftId || 'lifetime_lamb_tripe',
      scoopOn: this.scoopOn,
      extrasOn: { ...this.extrasOn },
      freeGiftOff: this.freeGiftOff || '',
      largeDogOffer: this.largeDogOffer,
      largeDogOfferDeadline: this.largeDogOfferDeadline,
      largeDogOfferWarned: !!this.largeDogOfferWarned,
      w3PlanDog: Number.isFinite(Number(this.w3PlanDog)) ? Number(this.w3PlanDog) : 0
    };
  }

  loadState(state) {
    if (state.currentStep === 'plan_snacks') state.currentStep = 'extras';
    if (state.currentStep && state.currentStep !== 'get_started') this.currentStep = state.currentStep;
    if (state.petData) this.petData = { ...this.petData, ...state.petData };
    if (typeof this.petData.name === 'string') {
      this.petData.name = normalizePetNameEdges(this.petData.name);
    }
    if (state.calculations) this.calculations = { ...this.calculations, ...state.calculations };
    if (state.selectedFlavorsTrial) this.selectedFlavorsTrial = [...state.selectedFlavorsTrial];
    if (state.selectedFlavorsFull) this.selectedFlavorsFull = [...state.selectedFlavorsFull];
    if (state.selectedPlan) this.selectedPlan = state.selectedPlan;
    if (state.planBowlValue !== undefined && state.planBowlValue !== null) this.planBowlValue = state.planBowlValue;
    if (state.planBowlOptionValues && state.planBowlOptionValues.length) {
      this.planBowlOptionValues = [...state.planBowlOptionValues];
    }
    if (state.selectedPricePeriod) this.selectedPricePeriod = state.selectedPricePeriod;
    if (state.petMetaobjectId) this.petMetaobjectId = state.petMetaobjectId;
    if (state.customerId) this.customerId = state.customerId;
    if (state.sessionCustomerId) this.sessionCustomerId = state.sessionCustomerId;
    if (state.sessionOwnerName !== undefined) this.sessionOwnerName = state.sessionOwnerName;
    if (state.sessionOwnerEmail !== undefined) this.sessionOwnerEmail = state.sessionOwnerEmail;
    if ((!this.petData.ownerEmail || !String(this.petData.ownerEmail).trim()) && this.sessionOwnerEmail) {
      this.petData.ownerEmail = this.sessionOwnerEmail;
    }
    if (state.sessionEmailConsent !== undefined) this.sessionEmailConsent = state.sessionEmailConsent;
    if (state.skippedEmailWithoutAddress !== undefined) {
      this.skippedEmailWithoutAddress = state.skippedEmailWithoutAddress;
    }
    if (state.wizardEmailMarketingConsentPreference === true || state.wizardEmailMarketingConsentPreference === false) {
      this.wizardEmailMarketingConsentPreference = state.wizardEmailMarketingConsentPreference;
    } else if (
      !(this.wizardEmailMarketingConsentPreference === true || this.wizardEmailMarketingConsentPreference === false)
    ) {
      try {
        const r = localStorage.getItem('wizard-email-marketing-consent-preference');
        if (r === '1') this.wizardEmailMarketingConsentPreference = true;
        else if (r === '0') this.wizardEmailMarketingConsentPreference = false;
      } catch (e) {}
    }
    if (state.variantPricing) this.variantPricing = { ...state.variantPricing };
    if (state.variantPricingHalf) this.variantPricingHalf = { ...state.variantPricingHalf };
    if (state.presentmentCurrencyCode) {
      this.presentmentCurrencyCode = String(state.presentmentCurrencyCode).trim().toUpperCase();
    }
    if (state.sellingPlanNames) this.sellingPlanNames = { ...state.sellingPlanNames };
    if (state.wizardOfferBarPct != null && Number.isFinite(Number(state.wizardOfferBarPct))) {
      this.wizardOfferBarPct = Math.round(Number(state.wizardOfferBarPct));
    } else {
      this.wizardOfferBarPct = null;
    }
    if (typeof state.wizardOfferBarFingerprint === 'string' && state.wizardOfferBarFingerprint.length) {
      this.wizardOfferBarFingerprint = state.wizardOfferBarFingerprint;
    } else {
      this.wizardOfferBarFingerprint = null;
    }
    if (Array.isArray(state.dogs) && state.dogs.length) {
      this.dogs = state.dogs.map((dog) => this.blankDog(dog));
      this.syncLeadDog();
    }
    if (Array.isArray(state.removedDogs)) {
      this.removedDogs = state.removedDogs.map((dog) => this.blankDog(dog));
    }
    if (state.largeDogOffer === 'accept' || state.largeDogOffer === 'decline' || state.largeDogOffer === 'expired') {
      this.largeDogOffer = state.largeDogOffer;
    }
    var deadline = Number(state.largeDogOfferDeadline);
    if (Number.isFinite(deadline) && deadline > 0) {
      this.largeDogOfferDeadline = deadline;
    }
    this.largeDogOfferWarned = !!state.largeDogOfferWarned;
    if (state.planPortion === 'mostly' || state.planPortion === 'complete') {
      this.planPortion = state.planPortion;
    }
    if (state.boxSize === 'medium' || state.boxSize === 'groot' || state.boxSize === 'extra') {
      this.boxSize = state.boxSize;
    }
    if (state.lifetimeGiftId) this.lifetimeGiftId = state.lifetimeGiftId;
    if (state.scoopOn === false || state.scoopOn === true) this.scoopOn = state.scoopOn;
    if (state.extrasOn && typeof state.extrasOn === 'object') this.extrasOn = { ...state.extrasOn };
    if (typeof state.freeGiftOff === 'string') this.freeGiftOff = state.freeGiftOff;
    if (Number.isFinite(Number(state.w3PlanDog))) this.w3PlanDog = Number(state.w3PlanDog);
    // Migrate legacy selectedFlavors to selectedFlavorsTrial/Full
    if (state.selectedFlavors && state.selectedFlavors.length > 0 && this.selectedFlavorsTrial.length === 0) {
      this.selectedFlavorsTrial = [...state.selectedFlavors];
      this.selectedFlavorsFull = [...state.selectedFlavors];
    }
    // Legacy: half plan lived on selectedPlan — bowl is now planBowlValue
    if (this.selectedPlan === 'half') {
      if (!this.planBowlValue) this.planBowlValue = 'Half';
      this.selectedPlan = '1';
    }
    if (!['1', '2', '3'].includes(String(this.selectedPlan || ''))) {
      this.selectedPlan = '1';
    }
  }

  reset() {
    const preservedSessionCustomerId = this.sessionCustomerId;
    const preservedSessionOwnerName = this.sessionOwnerName;
    const preservedSessionOwnerEmail = this.sessionOwnerEmail;
    const preservedSessionEmailConsent = this.sessionEmailConsent;
    const preservedWizardEmailMarketingConsentPreference = this.wizardEmailMarketingConsentPreference;

    this.currentStep = 'name';
    this.petData = {
      name: '',
      gender: '',
      spayed: null,
      ageGroup: '',
      breed: '',
      breedUnknown: false,
      breedMixed: false,
      weight: null,
      composition: '',
      activity: '',
      isWorkingDog: false,
      allergies: [],
      snacks: '',
      ownerName: preservedSessionOwnerName || '',
      ownerEmail: preservedSessionOwnerEmail || '',
      emailConsent: preservedSessionEmailConsent !== undefined ? preservedSessionEmailConsent : true
    };
    this.calculations = {
      dailyKcal: null,
      dailyFoodG: null,
      daysPerBag: null,
      bagCount: null,
      matchDays: null,
      introBagCount: null,
      introMatchDays: null
    };
    this.selectedFlavorsTrial = [];
    this.selectedFlavorsFull = [];
    this.selectedPlan = null;
    this.planBowlValue = null;
    this.planBowlOptionValues = null;
    this.selectedPricePeriod = 'day';
    this.petMetaobjectId = null;
    this.customerId = null;
    this.variantPricing = {};
    this.variantPricingHalf = {};
    this.presentmentCurrencyCode = null;
    this.sessionCustomerId = preservedSessionCustomerId;
    this.sessionOwnerName = preservedSessionOwnerName;
    this.sessionOwnerEmail = preservedSessionOwnerEmail;
    this.sessionEmailConsent = preservedSessionEmailConsent;
    this.skippedEmailWithoutAddress = false;
    this.wizardEmailMarketingConsentPreference = preservedWizardEmailMarketingConsentPreference;
    this.sellingPlanNames = {};
    this.dogs = [];
    this.removedDogs = [];
  }

  clearSessionCustomerId() {
    this.sessionCustomerId = null;
    this.sessionOwnerName = null;
    this.sessionOwnerEmail = null;
    this.sessionEmailConsent = true;
  }

  /**
   * Possessive pronoun for copy like "Name zijn/haar plan" (NL) or "his/her plan" (EN).
   */
  getPronoun() {
    const locale = (typeof window !== 'undefined' && window.__wizardCurrentLocale)
      ? String(window.__wizardCurrentLocale).toLowerCase()
      : 'nl';
    const en = locale.startsWith('en');
    if (this.petData.gender === 'female') {
      return en ? 'her' : 'haar';
    }
    return en ? 'his' : 'zijn';
  }

  getPossessivePronoun() {
    return this.getPronoun();
  }

  newStepAnswered(step) {
    const dogs = this.ensureDogs();
    if (step === 'food') return dogs.some((dog) => Array.isArray(dog.currentFood) && dog.currentFood.length > 0);
    if (step === 'eating') return dogs.some((dog) => !!dog.eating);
    if (step === 'issues') return dogs.some((dog) => !!dog.health);
    return true;
  }

  getFurthestValidStep() {
    const fallback = [
      'name', 'spayed', 'age', 'breed', 'food', 'eating', 'weight', 'composition',
      'activity', 'allergies', 'issues', 'snacks', 'email', 'calculation',
      'flavors', 'plan_overview', 'plan_onetime', 'extras', 'profile'
    ];
    const stepOrder = (window.BzWizard && typeof window.BzWizard.domSteps === 'function')
      ? window.BzWizard.domSteps()
      : fallback;
    const savedStep = this.currentStep || 'name';
    let savedStepIndex = stepOrder.indexOf(savedStep);
    if (savedStepIndex === -1) {
      const here = fallback.indexOf(savedStep);
      if (here === -1) return stepOrder[0] || 'name';
      const nextOn = stepOrder.findIndex((step) => fallback.indexOf(step) > here);
      savedStepIndex = nextOn === -1 ? stepOrder.length - 1 : Math.max(0, nextOn - 1);
    }

    let furthestValidStep = stepOrder[0] || 'name';
    for (let i = 0; i <= savedStepIndex; i++) {
      const step = stepOrder[i];
      if (step === 'calculation') {
        if (this.validateStep('email')) furthestValidStep = step;
        else break;
      } else if (step === 'plan_overview' || step === 'plan_onetime') {
        const w3 = window.BzWizard && window.BzWizard.id() === 'w3';
        const hasCalculations = this.calculations?.dailyFoodG != null;
        const hasTrialFlavors = this.selectedFlavorsTrial?.length > 0;
        const hasFullFlavors = this.selectedFlavorsFull?.length > 0;
        if (w3 ? this.validateStep('email') : (hasCalculations && (hasTrialFlavors || hasFullFlavors))) furthestValidStep = step;
        else break;
      } else if (step === 'profile' || step === 'extras' || step === 'flavors') {
        furthestValidStep = step;
      } else if ((step === 'food' || step === 'eating' || step === 'issues') && !this.newStepAnswered(step)) {
        if (step === savedStep) furthestValidStep = step;
        else break;
      } else {
        if (this.validateStep(step)) furthestValidStep = step;
        else break;
      }
    }
    return furthestValidStep;
  }
}

window.PetFlowState = PetFlowState;
